import type { GuardResult, MaybeAsync } from '@angular/router';
import { RedirectCommand, Router } from '@angular/router';
import { inject } from '@angular/core';
import type { ParameterValidator } from '../parameter-validator/parameter-validator';
import { isNil } from '@pct-ts/typeguard';

/**
 * Evaluates whether a parameter is valid based on the provided validation function, and handles redirection if invalid.
 * Can only be used by guards!
 *
 * @parameter {string} key - The name or identifier of the parameter being validated.
 * @parameter {string | undefined} parameter - The value of the parameter to be validated.
 * @parameter {ParameterValidator} parameterValidator - A function used to validate the parameter.
 * @parameter {string[]} [path] - Optional path to redirect to if the validation fails.
 * @return {MaybeAsync<GuardResult>} - Returns true if the parameter is valid, otherwise returns a RedirectCommand.
 */
export const hasParameterGuard = (
  key: string,
  parameter: string | undefined,
  parameterValidator: ParameterValidator,
  path?: string[],
): MaybeAsync<GuardResult> => {
  if (!isNil(parameter) && parameterValidator(parameter)) {
    return true;
  }
  console.error(`[hasParamGuard] The ${key} is not exist or not a valid.`);
  const router = inject(Router);
  return new RedirectCommand(router.parseUrl(path?.join('/') ?? '/'));
};
